<template>
	<div v-if="isLoading" class="spinner-container">
		<Spinner />
	</div>
	<Form
		v-else
		:validation-schema="validationSchema"
		class="form-container"
		@submit="addSplitDocumentOption"
		v-slot="{ errors }"
	>
		<div v-for="field in formFields" :key="field.name" :class="['form-input', field.width]">
			<label :for="field.name">{{ field.label }}</label>
			<Field
				v-if="field.type === 'select'"
				as="select"
				:id="field.name"
				:name="field.name"
				v-model="formData[field.name]"
				:disabled="isSubmitting"
			>
				<option></option>
				<option v-for="option in field.options" :key="option.id" :value="option.id">
					{{ option.name }}
				</option>
			</Field>
			<div v-else-if="field.type === 'checkbox'">
				<label :for="field.name">
					<Field :id="field.name" :name="field.name" v-model="formData[field.name]">
						<input
							type="checkbox"
							:id="field.name"
							:name="field.name"
							v-model="formData[field.name]"
							:disabled="isSubmitting"
						/>
					</Field>
					<span class="custom-checkbox"></span>
				</label>
			</div>
			<Field
				v-else
				:id="field.name"
				:name="field.name"
				:type="field.type"
				:disabled="isSubmitting"
				v-model="formData[field.name]"
			/>
			<ErrorMessage :name="field.name" class="error" />
		</div>

		<button class="secondary col-6" type="submit" :disabled="!isFormValid(errors) || isSubmitting">
			Add Split
		</button>

		<button
			class="secondary col-6"
			type="button"
			:disabled="splitDocumentOptions.length === 0 || isSubmitting"
			@click="removeAllOptions"
		>
			Remove All
		</button>

		<button
			class="submit-button col-12"
			type="button"
			:disabled="splitDocumentOptions.length === 0 || isSubmitting"
			@click="splitDocumentAsync"
		>
			Submit
		</button>
	</Form>

	<div class="options-container">
		<DocumentSplitOption
			v-for="(option, index) in splitDocumentOptions"
			:key="index"
			:options="option"
			:documentTypeOptions="documentTypeOptions"
			:borrowers="borrowers"
			@removeOption="handleRemoveOption"
		>
			Split Option #{{ index + 1 }}
		</DocumentSplitOption>
	</div>
</template>

<script setup lang="ts">
	import { ref, onMounted, reactive, computed, watchEffect } from 'vue';
	import axios from 'axios';
	import functions from '@/use/functions';
	import { Form, Field, ErrorMessage } from 'vee-validate';
	import { Borrower, DocumentProperties, SplitDocumentOptions } from '@/models/loans';
	import { useFormFields } from '@/composables/loan/documents/useSplitDocumentForm';
	import { useDocumentData } from '@/composables/loan/documents/useDocumentData';
	import Spinner from '@/components/shared/Spinner.vue';
	import DocumentSplitOption from './DocumentSplitOption.vue';

	const props = defineProps<{
		documentId: number;
		borrowers: Borrower[];
	}>();

	const isLoading = ref<boolean>(false);
	const isSubmitting = ref<boolean>(false);
	const documentProperties = ref<DocumentProperties>({
		pageCount: 0
	});

	const splitDocumentOptions = ref<SplitDocumentOptions[]>([]);

	const { documentTypeOptions } = useDocumentData();

	const { formFields, validationSchema } = useFormFields({
		properties: documentProperties,
		borrowers: props.borrowers,
		documentTypeOptions
	});

	const formData = reactive<Record<string, any>>({});

	const isFormValid = computed(() => {
		return (errors: Partial<Record<string, string | undefined>>) => errors && Object.keys(errors).length === 0;
	});

	function handleRemoveOption(option: SplitDocumentOptions) {
		const index = splitDocumentOptions.value.indexOf(option);

		if (index > -1) {
			splitDocumentOptions.value.splice(index, 1);
		}
	}

	const removeAllOptions = () => (splitDocumentOptions.value = []);

	async function getDocumentPropertiesAsync() {
		isLoading.value = true;

		try {
			var response = await axios.get(`/api/loandocuments/${props.documentId}/pdf-properties`);

			documentProperties.value = response.data;
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue getting the document properties. Please try again later.'
			});
		} finally {
			isLoading.value = false;
		}
	}

	function addSplitDocumentOption(formData: Record<string, any>) {
		try {
			splitDocumentOptions.value.push({
				documentType: formData['documentTypeId'],
				applicantId: formData['borrowerId'],
				startIndex: formData['startIndex'] - 1,
				endIndex: formData['endIndex'] - 1,
				applicantViewable: formData['borrowerVisible'] as boolean
			} as SplitDocumentOptions);
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue adding the split options. Please try again later.'
			});
		}
	}

	async function splitDocumentAsync() {
		isSubmitting.value = true;

		try {
			const request = {
				documentId: props.documentId,
				entityTypeId: 2, // loan
				options: splitDocumentOptions.value
			};

			await axios.post(`/api/loandocuments/${props.documentId}/split`, request);
		} catch (error) {
			functions.openModal({
				title: 'Oops!',
				description: 'There was an issue splitting the document. Please try again later.'
			});
		} finally {
			isSubmitting.value = false;
		}
	}

	watchEffect(() => {
		formFields.value.forEach((field) => {
			formData[field.name] = field.value;
		});
	});

	onMounted(async () => {
		await getDocumentPropertiesAsync();
	});
</script>

<style scoped src="@/assets/css/loans/form.css"></style>

<style scoped>
	.spinner-container {
		display: flex;
		justify-content: center;
		margin-top: 5rem;
	}

	.options-container {
		margin-top: 16px;
		overflow-y: hidden;
	}
</style>
